import {useEffect, useMemo, useRef, useState} from "react";

// Api
import {GetVideosBySeason} from "../../../../Api/Video";

// Models
import VideoDTO from "../../../../Models/DTOs/VideoDTO";
import CountWrapper from "../../../../Models/DTOs/CountWrapper";
import {sleep} from "../../../../Helpers/Performance/DelayAndDebounce";

interface Collection {
    key: number;
    label: string;
    episodes: VideoDTO[];
    count?: number; // keep it, but don’t trust it for paging
}

interface Options {
    take?: number;
    apiDelay: number;
}

export function useFetchVideosBySeason(
    seriesId: number,
    yearly: boolean,
    seasonCount: number,
    activeKey: number | null,
    buildLabelForKey: (seasonKey: number) => string,
    {take = 25, apiDelay = 0}: Options
) {
    const
        [collections, setCollections] = useState<Collection[]>([]),
        [loading, setLoading] = useState(false),
        [error, setError] = useState<Error>(),
        [loadingMap, setLoadingMap] = useState<Record<number, boolean>>({}),

        // Prevent stale closures in guard checks
        collectionsRef = useRef(collections);

    useEffect(() => {
        collectionsRef.current = collections;
    }, [collections]);

    // Reset cache when core identity changes
    useEffect(() => {
        setCollections([]);
        setLoadingMap({});
        setError(undefined);
        setLoading(false);
    }, [seriesId, yearly, seasonCount]);

    useEffect(() => {
        if (!seriesId) return;
        if (!seasonCount || seasonCount < 1) return;
        if (activeKey == null) return;
        if (activeKey < 1 || activeKey > seasonCount) return;

        // Cached? Don’t refetch.
        if (collectionsRef.current.some((c) => c.key === activeKey)) return;

        const abortCtrl = new AbortController();
        let cancelled = false;

        (async () => {
            try {
                setError(undefined);
                setLoading(true);
                setLoadingMap((m) => ({...m, [activeKey]: true}));

                const label = buildLabelForKey(activeKey);

                await sleep(apiDelay, abortCtrl.signal);

                // Create the collection entry immediately so UI can show progressive results
                setCollections((prev) => [...prev, {key: activeKey, label, episodes: [], count: undefined}]);

                let skip = 0;
                let pageCountMaybe: number | undefined = undefined;

                // Guard against API bugs where skip is ignored and you keep getting the same page
                let lastFirstId: number | null = null;
                let samePageHits = 0;

                while (true) {
                    if (cancelled) return;

                    const page: CountWrapper<VideoDTO> = await GetVideosBySeason(
                        seriesId,
                        activeKey,
                        take,
                        skip,
                        yearly ? 'desc' : 'asc',
                        abortCtrl
                    );

                    if (cancelled) return;

                    const items = page.items ?? [];
                    pageCountMaybe = page.count; // keep for display/metadata only

                    // Infinite-loop guard: if the API ignores skip and returns the same first item repeatedly
                    const thisFirstId = items[0]?.Id ?? null;
                    if (thisFirstId !== null && thisFirstId === lastFirstId) {
                        samePageHits++;
                        if (samePageHits >= 2) {
                            // Stop — we’re not making progress
                            break;
                        }
                    } else {
                        samePageHits = 0;
                        lastFirstId = thisFirstId;
                    }

                    // Append progressively so the grid grows as pages load
                    if (items.length) {
                        await sleep(apiDelay, abortCtrl.signal);

                        setCollections((prev) =>
                            prev.map((c) =>
                                c.key === activeKey
                                    ? {...c, episodes: [...c.episodes, ...items], count: pageCountMaybe}
                                    : c
                            )
                        );
                    } else {
                        // no items => done
                        break;
                    }

                    // The one paging rule that always works:
                    // if you got less than requested, there are no more pages.
                    if (items.length < take) break;

                    skip += take;

                    // Safety cap (optional): if someone sets take tiny and there are thousands, avoid runaway
                    if (skip > 5000) break;
                }
            } catch (e) {
                if (!cancelled) setError(e as Error);

                // If we created an empty placeholder collection entry, remove it on error
                setCollections((prev) => prev.filter((c) => c.key !== activeKey));
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setLoadingMap((m) => ({...m, [activeKey]: false}));
                }
            }
        })();

        return () => {
            cancelled = true;
            abortCtrl.abort();
        };
    }, [seriesId, yearly, seasonCount, activeKey, take, buildLabelForKey]);

    const active = useMemo(() => {
        if (activeKey == null) return null;
        return collections.find((c) => c.key === activeKey) ?? null;
    }, [collections, activeKey]);

    return {collections, active, loading, error, loadingMap};
}
